/**
 * @file        src/pages/airports/AirportList.jsx
 * @description Paginated airport directory with filter row.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

export default function AirportList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState({ code: '', city: '', country: '' });
  const [applied, setApplied] = useState(filters);

  const qc = useQueryClient();
  const params = { ...applied, page, pageSize };
  const { data, isLoading } = useQuery({
    queryKey: qk.airports.list(params),
    queryFn: () => get('/airports', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/airports/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Airport removed.');
      qc.invalidateQueries({ queryKey: ['airports'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete airport'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setApplied(filters);
  }

  async function onDelete(row) {
    if (!(await confirm(`Delete ${row.code} — ${row.name}?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Airport List"
        subtitle="Airport Management · global directory"
        actions={
          <Link to="/airports/new" className="btn primary"><Icon name="plus" /> Add Airport</Link>
        }
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={applyFilters}>
          <div className="field">
            <label>Airport Code</label>
            <input placeholder="e.g. DEL" value={filters.code}
                   onChange={(e) => setFilters({ ...filters, code: e.target.value })} />
          </div>
          <div className="field">
            <label>City</label>
            <input placeholder="Enter city" value={filters.city}
                   onChange={(e) => setFilters({ ...filters, city: e.target.value })} />
          </div>
          <div className="field">
            <label>Country</label>
            <input placeholder="Enter country" value={filters.country}
                   onChange={(e) => setFilters({ ...filters, country: e.target.value })} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Airport</th><th>City</th><th>Country</th><th>Code</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No airports match those filters.</td></tr>
                ) : rows.map((a, idx) => (
                  <tr key={a.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td><b>{a.code}</b><small className="muted db">{a.name}</small></td>
                    <td>{a.city}</td>
                    <td><Chip tone="ok">{a.country}</Chip></td>
                    <td className="mono">{a.countrycode}</td>
                    <td className="row-act">
                      <Link to={`/airports/${a.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(a)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
